import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/**
 * Fallback list used before the dynamic locale list is fetched from
 * /api/v1/i18n/locales (and if that request fails). Admins can add or
 * remove languages at runtime; the live list always wins.
 */
export const SUPPORTED_LANGUAGES = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
    { code: 'fr', label: 'Français' },
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

export interface LanguageOption {
    code: string;
    label: string;
}

interface LanguageContextValue {
    language: string;
    setLanguage: (code: string) => void;
    t: (key: string) => string;
    loaded: boolean;
    /** Live list of available languages (fetched from the i18n API). */
    languages: LanguageOption[];
}

const LanguageContext = createContext<LanguageContextValue>({
    language: 'en',
    setLanguage: () => {},
    t: (key: string) => key,
    loaded: false,
    languages: [...SUPPORTED_LANGUAGES],
});

const STORAGE_KEY = 'everythingslive_language';

/**
 * Timezone → language hints used only when the browser's own language
 * preferences don't match any supported locale. Kept intentionally small:
 * clearly single-language regions (Spanish-speaking Americas, Francophone
 * Europe/Africa). Bilingual regions (e.g. Canada) are deliberately omitted
 * so we never guess wrong there.
 */
const TIMEZONE_LANGUAGE_HINTS: { prefix: string; lang: string }[] = [
    // Spanish-speaking Americas
    { prefix: 'America/Mexico', lang: 'es' },
    { prefix: 'America/Guatemala', lang: 'es' },
    { prefix: 'America/Belize', lang: 'es' },
    { prefix: 'America/El_Salvador', lang: 'es' },
    { prefix: 'America/Honduras', lang: 'es' },
    { prefix: 'America/Nicaragua', lang: 'es' },
    { prefix: 'America/Costa_Rica', lang: 'es' },
    { prefix: 'America/Panama', lang: 'es' },
    { prefix: 'America/Havana', lang: 'es' },
    { prefix: 'America/Santo_Domingo', lang: 'es' },
    { prefix: 'America/Port-au-Prince', lang: 'es' },
    { prefix: 'America/Bogota', lang: 'es' },
    { prefix: 'America/Lima', lang: 'es' },
    { prefix: 'America/Quito', lang: 'es' },
    { prefix: 'America/Guayaquil', lang: 'es' },
    { prefix: 'America/Caracas', lang: 'es' },
    { prefix: 'America/Santiago', lang: 'es' },
    { prefix: 'America/Buenos_Aires', lang: 'es' },
    { prefix: 'America/Montevideo', lang: 'es' },
    { prefix: 'America/Asuncion', lang: 'es' },
    { prefix: 'America/La_Paz', lang: 'es' },
    // Francophone Europe / Africa
    { prefix: 'Europe/Paris', lang: 'fr' },
    { prefix: 'Europe/Brussels', lang: 'fr' },
    { prefix: 'Europe/Luxembourg', lang: 'fr' },
    { prefix: 'America/Montreal', lang: 'fr' },
    { prefix: 'Africa/Casablanca', lang: 'fr' },
    { prefix: 'Africa/Algiers', lang: 'fr' },
    { prefix: 'Africa/Tunis', lang: 'fr' },
    { prefix: 'Africa/Dakar', lang: 'fr' },
    { prefix: 'Africa/Abidjan', lang: 'fr' },
    { prefix: 'Africa/Lome', lang: 'fr' },
];

/**
 * Best-effort, dependency-free locale detection.
 *
 * Priority:
 *  1. Browser language preferences (navigator.languages / navigator.language),
 *     matched by base language code against the supported list.
 *  2. Timezone heuristics for clearly single-language regions.
 *  3. 'en' as the final fallback.
 */
export function detectLanguage(supported: string[]): string {
    const supportedSet = new Set(supported);

    // 1) Browser language preferences, most specific first.
    let candidates: string[] = [];
    try {
        if (typeof navigator !== 'undefined') {
            if (Array.isArray(navigator.languages) && navigator.languages.length > 0) {
                candidates = [...navigator.languages];
            } else if (navigator.language) {
                candidates = [navigator.language];
            }
        }
    } catch {
        // Non-browser environment — fall through to timezone/default.
    }

    for (const candidate of candidates) {
        const base = candidate.split('-')[0].toLowerCase();
        if (supportedSet.has(base)) {
            return base;
        }
    }

    // 2) Timezone heuristics (only when the browser gave no usable match).
    try {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (timeZone) {
            for (const hint of TIMEZONE_LANGUAGE_HINTS) {
                if (timeZone.startsWith(hint.prefix) && supportedSet.has(hint.lang)) {
                    return hint.lang;
                }
            }
        }
    } catch {
        // Intl unavailable — fall through to default.
    }

    // 3) Default.
    return 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
    // Read the stored preference once; auto-detection only runs when it is absent.
    const [storedPreference] = useState<string | null>(() => {
        try {
            return localStorage.getItem(STORAGE_KEY);
        } catch {
            return null;
        }
    });

    const [language, setLanguageState] = useState<string>(
        () => storedPreference ?? detectLanguage(SUPPORTED_LANGUAGES.map((l) => l.code)),
    );
    // True while the active language came from auto-detection (not an explicit choice).
    const autoDetectedRef = useRef(storedPreference === null);

    const [translations, setTranslations] = useState<Record<string, string>>({});
    const [loaded, setLoaded] = useState(false);
    const [languages, setLanguages] = useState<LanguageOption[]>([...SUPPORTED_LANGUAGES]);

    // Fetch the live list of available languages (admin-managed).
    useEffect(() => {
        let cancelled = false;

        const loadLocales = async () => {
            try {
                const res = await fetch('/api/v1/i18n/locales');
                if (!res.ok) return;
                const data = await res.json();
                const list: LanguageOption[] = Array.isArray(data?.locales) ? data.locales : [];

                if (cancelled || list.length === 0) return;

                setLanguages(list);

                // If the current language was auto-detected and is no longer
                // available (or a better match now exists), re-detect against
                // the full live list. Explicit user choices are never touched.
                if (autoDetectedRef.current && !list.some((l) => l.code === language)) {
                    const detected = detectLanguage(list.map((l) => l.code));
                    setLanguageState(detected);
                }
            } catch {
                // Non-fatal: keep the static fallback list.
            }
        };

        loadLocales();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load translations for the active language from the i18n API endpoint.
    useEffect(() => {
        let cancelled = false;
        setLoaded(false);

        const load = async () => {
            try {
                const res = await fetch(`/api/v1/i18n/${language}`);
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled && data && typeof data === 'object') {
                    setTranslations(data.translations || {});
                    setLoaded(true);
                }
            } catch {
                // Network errors are non-fatal: fall back to English keys.
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [language]);

    const setLanguage = useCallback((code: string) => {
        autoDetectedRef.current = false; // explicit choice from now on
        setLanguageState(code);
        try {
            localStorage.setItem(STORAGE_KEY, code);
        } catch {
            // Ignore storage failures (e.g. private browsing).
        }
    }, []);

    const t = useCallback(
        (key: string): string => {
            return translations[key] ?? key;
        },
        [translations],
    );

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t, loaded, languages }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage(): LanguageContextValue {
    return useContext(LanguageContext);
}
